/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { Grid, MenuItem, Select, FormControl, InputLabel, FormHelperText, Chip, Stack, Card, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Form } from "formik";
import * as Yup from "yup";
import { CurrencyEnum, LanguageEnum } from "@malmur-technologies/medicalsystem-shared";
import { CountryEnum } from "@malmur-technologies/medicalsystem-shared";
import Checkbox from "components/generals/Checkbox";
import FormField from "components/generals/FormInput";
import { useSelector, useDispatch } from "react-redux";
import { selectGeneralSettings, saveGeneralSettings } from "store/slices/generalSettingsSlice";
import { selectUserRoles } from "store/slices/auth.slice";
import { isRootUser } from "utils/rolePermissions.utils";
import { RootState } from "store/store";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import EventSlice from "store/slices/event.slice";
import { generalSettingsFormYUPValidation } from "utils/form.utils";

/**
 * Form values for general settings
 */
export interface GeneralSettingsFormValues {
    country: CountryEnum;
    language: LanguageEnum;
    currency: CurrencyEnum;
    isInsuranceModuleEnabled: boolean;
    isPharmacyModuleEnabled: boolean;
    isOfficeVisitModuleEnabled: boolean;
    isSystemSimplified: boolean;
    isPatientUserEnabled: boolean;
    defaultAppointmentDuration: number;
    videochatEnabledTime?: number | null;
    videochatEnabledAfterAppointmentTime?: number | null;
}

/**
 * Props for GeneralSettingsForm component
 */
interface GeneralSettingsFormProps {
    isModal?: boolean;
}

/**
 * General Settings Form component for managing system-wide configuration.
 * 
 * Features:
 * - Country selection
 * - Language preference
 * - Currency configuration (locked after initial setup)
 * - Insurance module toggle
 * - Pharmacy module toggle
 * - Office visit module toggle
 * - Simplified system mode toggle
 * - Default appointment duration (5-480 minutes)
 * - Videochat enabled time (0-120 minutes, optional)
 * - Form validation with Yup
 * - Direct save functionality
 * - Redux integration for state management
 * 
 * Security:
 * - Accessible only to ADMIN and ROOT users
 * - Currency field locked after initial configuration
 * 
 * @returns Editable general settings form with full width
 */
const GeneralSettingsForm: React.FC<GeneralSettingsFormProps> = ({ isModal = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const generalSettings = useSelector(selectGeneralSettings);
    const currentUserRoles = useSelector((state: RootState) => selectUserRoles(state));
    const isCurrentUserRoot = isRootUser(currentUserRoles);
    const { addSuccessMessage } = EventSlice.actions;

    // Initial form values from Redux state
    const initialValues: GeneralSettingsFormValues = {
        country: generalSettings.country as CountryEnum || CountryEnum.USA,
        language: generalSettings.language as LanguageEnum || LanguageEnum.ENGLISH,
        currency: generalSettings.currency as CurrencyEnum || CurrencyEnum.USD,
        isInsuranceModuleEnabled: generalSettings.isInsuranceModuleEnabled ?? false,
        isPharmacyModuleEnabled: generalSettings.isPharmacyModuleEnabled ?? false,
        isOfficeVisitModuleEnabled: generalSettings.isOfficeVisitModuleEnabled ?? false,
        isSystemSimplified: generalSettings.isSystemSimplified ?? false,
        isPatientUserEnabled: generalSettings.isPatientUserEnabled ?? true,
        defaultAppointmentDuration: generalSettings.defaultAppointmentDuration || 30,
        videochatEnabledTime: generalSettings.videochatEnabledTime ?? null,
        videochatEnabledAfterAppointmentTime: generalSettings.videochatEnabledAfterAppointmentTime ?? null,
    };

    const isFirstConfiguration = !generalSettings.country;

    // Country options
    const countryOptions = Object.values(CountryEnum).map((country) => ({
        label: t(`generalSettingsModal.${country}`),
        value: String(country),
    }));

    // Language options (exclude Chinese)
    const languageOptions = Object.values(LanguageEnum)
        .filter((language) => language !== LanguageEnum.CHINESE)
        .map((language) => ({
            label: t(`generalSettingsModal.${language}`),
            value: String(language),
        }));

    // Currency options
    const currencyOptions = Object.values(CurrencyEnum).map((currency) => ({
        label: String(currency),
        value: String(currency),
    }));

    const handleSubmit = async (values: GeneralSettingsFormValues) => {
        const parseNumericField = (value: any): number | null => {
            if (value === "" || value === undefined || value === null) return null;
            const parsed = typeof value === "string" ? parseInt(value, 10) : value;
            return isNaN(parsed) ? null : parsed;
        };

        const cleanedValues = {
            ...values,
            videochatEnabledTime: parseNumericField(values.videochatEnabledTime),
            videochatEnabledAfterAppointmentTime: parseNumericField(values.videochatEnabledAfterAppointmentTime),
        };

        try {
            const resultAction = await dispatch(saveGeneralSettings(cleanedValues));
            if (saveGeneralSettings.fulfilled.match(resultAction)) {
                dispatch(addSuccessMessage({ message: t("generalSettingsModal.success"), id: "general-settings-save-success" }));
            }
        } catch (error) {
            console.error("Error updating general settings:", error);
        }
    };

    return (
        <Box className="form-container">
            <FormikForm
                initialValues={initialValues}
                validationSchema={generalSettingsFormYUPValidation(t)}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                    <Form>
                        {!isModal && (
                            <H4 icon={<SettingsIcon />}>
                                {t("generalSettingsModal.title")}
                            </H4>
                        )}
                        <Grid container spacing={3}>
                            {/* Regional Configuration Card */}
                            <Grid item xs={12} md={6}>
                                <Card className="general-settings-form__card-equal-height">
                                    <CardContent>
                                        <Grid container spacing={5}>
                                            {/* Country Selection */}
                                            <Grid item xs={12} md={12}>
                                                <Typography className="general-settings-form__section-title">
                                                    {t("generalSettingsModal.subtitleGeographicSettings")}
                                                </Typography>
                                                <FormControl fullWidth variant="standard" error={touched.country && Boolean(errors.country)}>
                                                    <InputLabel id="country-label">{t("generalSettingsModal.country")}</InputLabel>
                                                    <Select
                                                        labelId="country-label"
                                                        name="country"
                                                        value={values.country}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    >
                                                        {countryOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {touched.country && errors.country && (
                                                        <FormHelperText>{errors.country}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                            {/* Language Selection */}
                                            <Grid item xs={12} md={12}>
                                                <FormControl fullWidth variant="standard" error={touched.language && Boolean(errors.language)}>
                                                    <InputLabel id="language-label">{t("generalSettingsModal.language")}</InputLabel>
                                                    <Select
                                                        labelId="language-label"
                                                        name="language"
                                                        value={values.language}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    >
                                                        {languageOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {touched.language && errors.language && (
                                                        <FormHelperText>{errors.language}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                            {/* Currency Selection */}
                                            <Grid item xs={12} md={12}>
                                                <FormControl fullWidth variant="standard" error={touched.currency && Boolean(errors.currency)}>
                                                    <InputLabel id="currency-label">{t("generalSettingsModal.currency")}</InputLabel>
                                                    <Select
                                                        labelId="currency-label"
                                                        name="currency"
                                                        value={values.currency}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        disabled={!isFirstConfiguration}
                                                    >
                                                        {currencyOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {touched.currency && errors.currency && (
                                                        <FormHelperText>{errors.currency}</FormHelperText>
                                                    )}
                                                    {!isFirstConfiguration && (
                                                        <Box className="general-settings-form__currency-lock-container">
                                                            <Chip
                                                                label={t("userProfilePage.locked")}
                                                                size="small"
                                                                color="warning"
                                                                className="general-settings-form__currency-chip"
                                                            />
                                                            <FormHelperText className="general-settings-form__helper-text-no-margin">
                                                                {t("generalSettingsModal.currencyLocked")}
                                                            </FormHelperText>
                                                        </Box>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Appointments & Videochat Configuration Card */}
                            <Grid item xs={12} md={6}>
                                <Card className="general-settings-form__card-equal-height">
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            {/* Default Appointment Duration */}
                                            <Grid item xs={12} md={12}>
                                                <Typography className="general-settings-form__section-title">
                                                    {t("generalSettingsModal.subtitleTimeSettings")}
                                                </Typography>
                                                <FormField
                                                    type="number"
                                                    name="defaultAppointmentDuration"
                                                    label={t("generalSettingsModal.defaultAppointmentDuration")}
                                                    value={values.defaultAppointmentDuration}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.defaultAppointmentDuration && Boolean(errors.defaultAppointmentDuration)}
                                                    InputProps={{
                                                        inputProps: { min: 5, max: 480 }
                                                    }}
                                                />
                                            </Grid>
                                            {/* Videochat Enabled Time (Optional) */}
                                            <Grid item xs={12} md={12}>
                                                <FormField
                                                    type="text"
                                                    name="videochatEnabledTime"
                                                    label={t("generalSettingsModal.videochatEnabledTime")}
                                                    value={values.videochatEnabledTime === null ? t("generalSettingsModal.none") : values.videochatEnabledTime ?? ""}
                                                    onFocus={(e) => {
                                                        if (values.videochatEnabledTime === null) {
                                                            setFieldValue("videochatEnabledTime", "");
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue === "") {
                                                            setFieldValue("videochatEnabledTime", "");
                                                        } else {
                                                            const numValue = Number(inputValue);
                                                            if (!isNaN(numValue) && inputValue.match(/^\d+$/)) {
                                                                setFieldValue("videochatEnabledTime", numValue);
                                                            }
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        handleBlur(e);
                                                        if (e.target.value === "") {
                                                            setFieldValue("videochatEnabledTime", null);
                                                        }
                                                    }}
                                                    error={touched.videochatEnabledTime && Boolean(errors.videochatEnabledTime)}
                                                    placeholder={t("generalSettingsModal.none")}
                                                />
                                            </Grid>
                                            {/* Videochat After Appointment Enabled Time (Optional) */}
                                            <Grid item xs={12} md={12}>
                                                <FormField
                                                    type="text"
                                                    name="videochatEnabledAfterAppointmentTime"
                                                    label={t("generalSettingsModal.videochatAfterAppointmentEnabledTime")}
                                                    value={values.videochatEnabledAfterAppointmentTime === null ? t("generalSettingsModal.none") : values.videochatEnabledAfterAppointmentTime ?? ""}
                                                    onFocus={(e) => {
                                                        if (values.videochatEnabledAfterAppointmentTime === null) {
                                                            setFieldValue("videochatEnabledAfterAppointmentTime", "");
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue === "") {
                                                            setFieldValue("videochatEnabledAfterAppointmentTime", "");
                                                        } else {
                                                            const numValue = Number(inputValue);
                                                            if (!isNaN(numValue) && inputValue.match(/^\d+$/)) {
                                                                setFieldValue("videochatEnabledAfterAppointmentTime", numValue);
                                                            }
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        handleBlur(e);
                                                        if (e.target.value === "") {
                                                            setFieldValue("videochatEnabledAfterAppointmentTime", null);
                                                        }
                                                    }}
                                                    error={touched.videochatEnabledAfterAppointmentTime && Boolean(errors.videochatEnabledAfterAppointmentTime)}
                                                    placeholder={t("generalSettingsModal.none")}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Module Configuration Card */}
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            {/* Module Configuration Section */}
                                            <Grid item xs={12}>
                                                <Box className="general-settings-form__module-section-container">
                                                    <H4>{t("generalSettingsModal.moduleConfiguration")}</H4>
                                                </Box>
                                            </Grid>
                                            {/* Insurance Module Enabled */}
                                            <Grid item xs={12} md={3}>
                                                <Box className="general-settings-form__checkbox-container">
                                                    <Checkbox
                                                        id="cy-insurance-module-checkbox"
                                                        label={t("generalSettingsModal.isInsuranceModuleEnabled")}
                                                        checked={values.isInsuranceModuleEnabled}
                                                        onChange={(e) => setFieldValue("isInsuranceModuleEnabled", e.target.checked)}
                                                    />
                                                </Box>
                                            </Grid>
                                            {/* Pharmacy Module Enabled */}
                                            <Grid item xs={12} md={3}>
                                                <Box className="general-settings-form__checkbox-container">
                                                    <Checkbox
                                                        id="cy-pharmacy-module-checkbox"
                                                        label={t("generalSettingsModal.isPharmacyModuleEnabled")}
                                                        checked={values.isPharmacyModuleEnabled}
                                                        onChange={(e) => setFieldValue("isPharmacyModuleEnabled", e.target.checked)}
                                                    />
                                                </Box>
                                            </Grid>
                                            {/* Office Visit Enabled */}
                                            <Grid item xs={12} md={3}>
                                                <Box className="general-settings-form__checkbox-container">
                                                    <Checkbox
                                                        id="cy-office-visit-module-checkbox"
                                                        label={t("generalSettingsModal.isOfficeVisitModuleEnabled")}
                                                        checked={values.isOfficeVisitModuleEnabled}
                                                        onChange={(e) => setFieldValue("isOfficeVisitModuleEnabled", e.target.checked)}
                                                    />
                                                </Box>
                                            </Grid>
                                            {/* System Simplified */}
                                            <Grid item xs={12} md={3}>
                                                <Box className="general-settings-form__checkbox-container">
                                                    <Checkbox
                                                        id="cy-system-simplified-checkbox"
                                                        label={t("generalSettingsModal.isSystemSimplified")}
                                                        checked={values.isSystemSimplified}
                                                        onChange={(e) => setFieldValue("isSystemSimplified", e.target.checked)}
                                                    />
                                                </Box>
                                            </Grid>
                                            {/* Patient User Enabled - Only visible for ROOT users */}
                                            {isCurrentUserRoot && (
                                                <Grid item xs={12} md={3}>
                                                    <Box className="general-settings-form__checkbox-container">
                                                        <Checkbox
                                                            id="cy-patient-user-enabled-checkbox"
                                                            label={t("generalSettingsModal.isPatientUserEnabled")}
                                                            checked={values.isPatientUserEnabled}
                                                            onChange={(e) => setFieldValue("isPatientUserEnabled", e.target.checked)}
                                                        />
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2} className="general-settings-form__actions-container">
                                    <Button
                                        id="cy-saveSettingSystem"
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        disabled={generalSettings.loading}
                                    >
                                        {generalSettings.loading ? t("generalSettingsModal.saving") : t("generalSettingsModal.save")}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </FormikForm>
        </Box>
    );
};
export default GeneralSettingsForm;
